import type { MatchOutcome, MatchState, PlayerId, RelaySample } from '@signalclash/shared';
import { DOCTRINE_DEFS, PLAYER_ONE, PLAYER_ZERO } from '@signalclash/shared';

import { analytics } from '../analytics/Analytics.js';
import { type UnlockTier } from '../save/CosmeticUnlocks.js';
import { computeMatchMedal, type Medal } from '../save/MatchMedals.js';
import type { RankUpdate } from '../save/Rank.js';
import type { XpAward } from '../save/Xp.js';

const MODAL_ID = 'end-of-match';
const STYLE_ID = 'end-of-match-styles';

interface EndOfMatchOptions {
  onRematch: () => void;
  onBackToMenu: () => void;
}

// Tracked outside the snapshot: per-match counters fed in from the
// presentation layer (Sfx/KillEvents) so the MVP card can highlight
// streak achievements the simulation doesn't model directly.
export interface ExtraMatchStats {
  viewerKills: number;
  enemyKills: number;
  bestStreak: number; // 1+ where 2=double, 3=triple, 4=rampage, 5+=godlike
}

// V1 EOM modal: outcome banner, side-by-side stats summary, and a relay-control
// sparkline driven by MatchState.stats.relayHistory. Rule 2: presentation only.
// All numbers come from the simulation's MatchStats — the UI never recomputes
// (no ad-hoc squad counts or peak-income arithmetic here).
//
// observeSnapshot() is a fallback path: if the host's match_ended lifecycle
// event is missed (subscriber added late) we still surface the modal once we
// see a snapshot whose outcome != null.
export class EndOfMatch {
  private readonly opts: EndOfMatchOptions;
  private root: HTMLDivElement | null = null;
  private bannerEl: HTMLDivElement | null = null;
  private detailEl: HTMLDivElement | null = null;
  private mvpEl: HTMLDivElement | null = null;
  private statsEl: HTMLDivElement | null = null;
  // V1.8 — cosmetic unlock celebration. Renders ONLY when this match's
  // completion advanced the player across a tier boundary (5/15/40
  // matches). Without this, the title unlocks invisibly and the
  // retention nudge is wasted.
  private unlockEl: HTMLDivElement | null = null;
  private pendingUnlock: UnlockTier | null = null;
  // V1.9 — next-tier preview. The "1 more match to unlock Veteran" hook
  // that CrazyGamesTricks §5.3 / §6.5 calls out: the moment the player
  // is deciding Rematch vs. Quit is exactly when "you're 3 away" should
  // land. Rendered as a slim chip below the unlock card; both can show
  // (just earned X, working toward Y).
  private nextTierEl: HTMLDivElement | null = null;
  private nextTier: UnlockTier | null = null;
  private nextTierMatchesToGo = 0;
  // V1.9 — per-match medal (gold/silver/bronze) computed at show()-time
  // from the final state + extra stats. Gives losers a measurable beat
  // ("Bronze — hard-fought") so a defeat doesn't end on a flat note.
  private medalEl: HTMLDivElement | null = null;
  // V1.9 — XP gain card. Awarded externally (ClientApp) so the cloud
  // sync write happens BEFORE the modal renders; we receive a pre-baked
  // XpAward struct and render the delta + bar.
  private xpEl: HTMLDivElement | null = null;
  private xpAward: XpAward | null = null;
  // V2 Tempo — Rank chip. Same input shape as the XP card: ClientApp
  // calls applyMatchResult() and routes the resulting RankUpdate here.
  // Rendered above the XP block so the climb story reads top-down
  // (rank first, XP next, daily/unlocks last).
  private rankEl: HTMLDivElement | null = null;
  private rankUpdate: RankUpdate | null = null;
  private rematchBtn: HTMLButtonElement | null = null;
  private menuBtn: HTMLButtonElement | null = null;
  private shown = false;
  // V4.1 — anchor for eom_* msShown analytics. Set on show(); reset on hide().
  // `lastOutcome` and `lastWon` are stashed so the unload-path eom_abandoned
  // event (fired via dispose / external hide) can carry the same outcomeKind
  // + won the original eom_shown did.
  private shownAtMs = 0;
  private lastOutcomeKind: MatchOutcome['kind'] | null = null;
  private lastWon = false;
  private interactionEmitted = false;
  // V4 polish: when the lifecycle handler wants to delay the modal so
  // the climactic VICTORY/DEFEAT announcer banner gets stage time, it
  // calls scheduleShow() which sets `pending` and arms a timer. While
  // pending the observeSnapshot fallback path no-ops, so the modal
  // can't race ahead of the banner.
  private pending = false;
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;
  // V2 network mode: the viewer's player slot is assigned by the server.
  // Mutable so the host (ClientApp) can flip it after the server sends
  // `assigned`. Default PLAYER_ZERO (local mode, single-client tests).
  private viewerId: PlayerId = PLAYER_ZERO;
  private extraStats: ExtraMatchStats = { viewerKills: 0, enemyKills: 0, bestStreak: 0 };

  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
  }

  setExtraStats(stats: ExtraMatchStats): void {
    this.extraStats = { ...stats };
  }

  // V1.8 — ClientApp computes which tier (if any) was crossed this match
  // by comparing pre- and post-match totalMatches, then routes it here so
  // the modal can celebrate. Cleared on hide(); a missed call simply
  // means the celebration card stays hidden for that match.
  setPendingUnlock(tier: UnlockTier | null): void {
    this.pendingUnlock = tier;
  }

  // V1.9 — the *upcoming* tier (computed from post-match totalMatches).
  // matchesToGo is the count remaining to reach it. When the player just
  // crossed a tier AND there's another tier ahead, both the unlock card
  // (above) and the preview chip render — the celebration and the next
  // carrot at the same moment. Pass null to hide.
  setNextTier(tier: UnlockTier | null, matchesToGo: number): void {
    this.nextTier = tier;
    this.nextTierMatchesToGo = Math.max(0, matchesToGo);
  }

  // V1.9 — XP award + level state for the match. ClientApp computes this
  // BEFORE calling show()/scheduleShow() so the modal can render the
  // delta and progress bar without owning the persistence write.
  setXpAward(award: XpAward | null): void {
    this.xpAward = award;
  }

  // V2 Tempo — Rank delta + tier-change celebration. Promoted = pulse
  // gold; demoted = subtle red rim. Hidden when no update is set.
  setRankUpdate(update: RankUpdate | null): void {
    this.rankUpdate = update;
  }

  constructor(opts: EndOfMatchOptions) {
    this.opts = opts;
    injectStyles();
    this.mount();
    this.hide();
  }

  show(outcome: MatchOutcome, finalState: MatchState | null): void {
    if (this.shown) return;
    this.shown = true;
    this.shownAtMs = Date.now();
    this.lastOutcomeKind = outcome.kind;
    this.lastWon = outcome.winner === this.viewerId;
    this.interactionEmitted = false;
    // V4.1 — eom_shown is the funnel-arrival event for the post-match
    // decision moment. Rematch / Menu / Abandoned events all close out
    // against this.
    analytics.emit({
      kind: 'eom_shown',
      outcomeKind: outcome.kind,
      won: this.lastWon,
    });
    if (this.root === null || this.bannerEl === null || this.detailEl === null) return;
    this.bannerEl.textContent = this.lastWon ? 'VICTORY' : 'DEFEAT';
    this.bannerEl.dataset.win = String(this.lastWon);
    this.detailEl.innerHTML = describeOutcomeHtml(
      outcome,
      finalState,
      this.viewerId,
      this.lastWon,
      this.extraStats,
    );
    this.renderMedal(finalState);
    this.renderMvp(finalState, this.lastWon);
    this.renderUnlock();
    this.renderNextTier();
    this.renderRank();
    this.renderXp();
    this.renderStats(finalState);
    this.root.style.display = 'flex';
    this.rematchBtn?.focus();
  }

  private renderUnlock(): void {
    if (this.unlockEl === null) return;
    const tier = this.pendingUnlock;
    if (tier === null) {
      this.unlockEl.style.display = 'none';
      this.unlockEl.innerHTML = '';
      return;
    }
    this.unlockEl.style.display = 'block';
    this.unlockEl.innerHTML = `
      <div class="eom__unlock-chip">NEW TITLE UNLOCKED</div>
      <div class="eom__unlock-title">${escapeText(tier.title)}</div>
      <div class="eom__unlock-desc">${escapeText(tier.description)}</div>
    `;
    analytics.emit({
      kind: 'unlock_reached',
      tierId: tier.title,
      totalMatches: tier.matchThreshold,
    });
  }

  private renderNextTier(): void {
    if (this.nextTierEl === null) return;
    const tier = this.nextTier;
    if (tier === null || this.nextTierMatchesToGo <= 0) {
      this.nextTierEl.style.display = 'none';
      this.nextTierEl.innerHTML = '';
      return;
    }
    const to = this.nextTierMatchesToGo;
    const matchWord = to === 1 ? 'match' : 'matches';
    this.nextTierEl.style.display = 'flex';
    this.nextTierEl.innerHTML = `
      <span class="eom__next-chip">NEXT</span>
      <span class="eom__next-text">${to} more ${matchWord} to unlock <strong>${escapeText(tier.title)}</strong></span>
    `;
  }

  private renderMedal(finalState: MatchState | null): void {
    if (this.medalEl === null) return;
    const medal: Medal | null = computeMatchMedal({
      finalState,
      viewerId: this.viewerId,
      viewerKills: this.extraStats.viewerKills,
      won: this.lastWon,
    });
    if (medal === null) {
      this.medalEl.style.display = 'none';
      this.medalEl.innerHTML = '';
      return;
    }
    this.medalEl.style.display = 'flex';
    this.medalEl.dataset.tier = medal.tier;
    this.medalEl.innerHTML = `
      <span class="eom__medal-badge">${medalGlyph(medal.tier)}</span>
      <span class="eom__medal-text">
        <span class="eom__medal-label">${escapeText(medal.label)}</span>
        <span class="eom__medal-detail">${escapeText(medal.detail)}</span>
      </span>
    `;
    analytics.emit({
      kind: 'eom_medal_shown',
      tier: medal.tier,
      won: this.lastWon,
    });
  }

  private renderRank(): void {
    if (this.rankEl === null) return;
    const update = this.rankUpdate;
    if (update === null) {
      this.rankEl.style.display = 'none';
      this.rankEl.innerHTML = '';
      return;
    }
    const tier = update.afterTier;
    const deltaSign = update.delta > 0 ? '+' : '';
    const deltaClass = update.delta > 0
      ? 'eom__rank-delta--up'
      : update.delta < 0
        ? 'eom__rank-delta--down'
        : 'eom__rank-delta--flat';
    const promoBadge = update.promoted
      ? `<span class="eom__rank-promo">PROMOTED → ${escapeText(tier.name.toUpperCase())}</span>`
      : update.demoted
        ? `<span class="eom__rank-demo">DEMOTED → ${escapeText(tier.name.toUpperCase())}</span>`
        : update.newPeak
          ? `<span class="eom__rank-peak">NEW PEAK</span>`
          : '';
    this.rankEl.classList.toggle('eom__rank--promoted', update.promoted);
    this.rankEl.classList.toggle('eom__rank--demoted', update.demoted);
    this.rankEl.style.display = 'block';
    this.rankEl.style.setProperty('--rank-color', tier.color);
    this.rankEl.style.setProperty('--rank-glow', tier.glow);
    this.rankEl.innerHTML = `
      <div class="eom__rank-head">
        <span class="eom__rank-tier" style="color:${tier.color};text-shadow:0 0 12px ${tier.glow}">${escapeText(tier.name.toUpperCase())}</span>
        <span class="eom__rank-elo">${update.afterElo} ELO</span>
        <span class="eom__rank-delta ${deltaClass}">${deltaSign}${update.delta}</span>
      </div>
      ${promoBadge}
    `;
  }

  private renderXp(): void {
    if (this.xpEl === null) return;
    const award = this.xpAward;
    if (award === null || award.delta <= 0) {
      this.xpEl.style.display = 'none';
      this.xpEl.innerHTML = '';
      return;
    }
    const { after, delta, leveledUp, breakdown } = award;
    const pct = after.xpToNextLevel > 0
      ? Math.max(0, Math.min(100, (after.xpIntoLevel / after.xpToNextLevel) * 100))
      : 100;
    const breakdownChips: string[] = [];
    if (breakdown.outcome > 0) {
      breakdownChips.push(
        `<span class="eom__xp-chip">${this.lastWon ? 'Win' : 'Played'} +${breakdown.outcome}</span>`,
      );
    }
    if (breakdown.relays > 0) {
      breakdownChips.push(`<span class="eom__xp-chip">Relays +${breakdown.relays}</span>`);
    }
    if (breakdown.squads > 0) {
      breakdownChips.push(`<span class="eom__xp-chip">Squads +${breakdown.squads}</span>`);
    }
    if (breakdown.kills > 0) {
      breakdownChips.push(`<span class="eom__xp-chip">Kills +${breakdown.kills}</span>`);
    }
    const levelUpBadge = leveledUp
      ? '<span class="eom__xp-levelup">LEVEL UP</span>'
      : '';
    this.xpEl.style.display = 'block';
    this.xpEl.innerHTML = `
      <div class="eom__xp-head">
        <span class="eom__xp-level">LV ${after.level}</span>
        <span class="eom__xp-delta">+${delta} XP</span>
        ${levelUpBadge}
      </div>
      <div class="eom__xp-bar"><div class="eom__xp-fill" style="width:${pct.toFixed(1)}%"></div></div>
      <div class="eom__xp-progress">${after.xpIntoLevel} / ${after.xpToNextLevel} to Lv ${after.level + 1}</div>
      <div class="eom__xp-breakdown">${breakdownChips.join('')}</div>
    `;
    analytics.emit({
      kind: 'eom_xp_awarded',
      delta,
      level: after.level,
      leveledUp,
    });
  }

  // V4 polish — delayed show so the climactic VICTORY/DEFEAT announcer
  // banner gets stage time before the stats card fades up under it.
  // Marks `pending` immediately so observeSnapshot can't race ahead.
  scheduleShow(outcome: MatchOutcome, finalState: MatchState | null, delayMs: number): void {
    if (this.shown || this.pending) return;
    this.pending = true;
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      this.pending = false;
      this.show(outcome, finalState);
    }, delayMs);
  }

  hide(): void {
    // V4.1 — if the modal was on screen and the player navigated away
    // without clicking Rematch / Menu (e.g. they returned to menu via a
    // hotkey, the scene shut down, or external code dismissed), emit
    // eom_abandoned so the dashboard can see how much of the post-match
    // decision moment was real engagement vs. silent exits.
    if (this.shown && !this.interactionEmitted && this.lastOutcomeKind !== null) {
      analytics.emit({
        kind: 'eom_abandoned',
        msShown: Math.max(0, Date.now() - this.shownAtMs),
        won: this.lastWon,
      });
      this.interactionEmitted = true;
    }
    if (this.root !== null) this.root.style.display = 'none';
    this.shown = false;
    if (this.pendingTimer !== null) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
    this.pending = false;
    // V1.8 — clear the unlock so the next match doesn't re-celebrate
    // a stale tier. ClientApp re-sets this before show() for the match
    // that actually crossed the boundary.
    this.pendingUnlock = null;
    if (this.unlockEl !== null) {
      this.unlockEl.style.display = 'none';
      this.unlockEl.innerHTML = '';
    }
    // V1.9 — symmetric clears for the new render slots so a re-show
    // with different inputs doesn't bleed stale content.
    this.nextTier = null;
    this.nextTierMatchesToGo = 0;
    if (this.nextTierEl !== null) {
      this.nextTierEl.style.display = 'none';
      this.nextTierEl.innerHTML = '';
    }
    if (this.medalEl !== null) {
      this.medalEl.style.display = 'none';
      this.medalEl.innerHTML = '';
    }
    this.xpAward = null;
    if (this.xpEl !== null) {
      this.xpEl.style.display = 'none';
      this.xpEl.innerHTML = '';
    }
  }

  reset(): void {
    this.hide();
  }

  observeSnapshot(snap: MatchState): void {
    if (this.pending) return;
    if (snap.outcome !== null) this.show(snap.outcome, snap);
  }

  private mount(): void {
    this.root = ensureRoot();
    this.root.innerHTML = '';
    const card = document.createElement('div');
    card.className = 'eom__card';

    this.bannerEl = document.createElement('div');
    this.bannerEl.className = 'eom__banner';
    card.appendChild(this.bannerEl);

    this.detailEl = document.createElement('div');
    this.detailEl.className = 'eom__detail';
    card.appendChild(this.detailEl);

    // V1.9 — medal chip sits BETWEEN the detail line and the MVP card so
    // the player's first eye-scan after the banner sees a measurable
    // grade (gold/silver/bronze) rather than just the prose summary.
    this.medalEl = document.createElement('div');
    this.medalEl.className = 'eom__medal';
    this.medalEl.style.display = 'none';
    card.appendChild(this.medalEl);

    // Player-of-the-match card. Computes the single most-notable
    // achievement of the match and surfaces it as a hero stat so the
    // celebratory beat lands before the player scans the dense stats
    // table below.
    this.mvpEl = document.createElement('div');
    this.mvpEl.className = 'eom__mvp';
    card.appendChild(this.mvpEl);

    this.unlockEl = document.createElement('div');
    this.unlockEl.className = 'eom__unlock';
    this.unlockEl.style.display = 'none';
    card.appendChild(this.unlockEl);

    // V1.9 — next-tier preview chip. Renders below the unlock celebration
    // (or in its place when no tier was crossed this match) so the
    // post-match decision moment has a visible carrot.
    this.nextTierEl = document.createElement('div');
    this.nextTierEl.className = 'eom__next';
    this.nextTierEl.style.display = 'none';
    card.appendChild(this.nextTierEl);

    // V2 Tempo — Rank chip. Sits ABOVE the XP card so the climb story
    // reads top-down. Pulses gold on tier-up, red-rims on demotion.
    this.rankEl = document.createElement('div');
    this.rankEl.className = 'eom__rank';
    this.rankEl.style.display = 'none';
    card.appendChild(this.rankEl);

    // V1.9 — XP gain card. Always reserves layout space when an award
    // exists; collapsed when no XP changed (defensive — currently every
    // completed match awards XP).
    this.xpEl = document.createElement('div');
    this.xpEl.className = 'eom__xp';
    this.xpEl.style.display = 'none';
    card.appendChild(this.xpEl);

    this.statsEl = document.createElement('div');
    this.statsEl.className = 'eom__stats';
    card.appendChild(this.statsEl);

    const buttons = document.createElement('div');
    buttons.className = 'eom__buttons';
    this.rematchBtn = document.createElement('button');
    this.rematchBtn.type = 'button';
    this.rematchBtn.className = 'eom__btn eom__btn--primary';
    this.rematchBtn.textContent = 'Rematch';
    this.rematchBtn.addEventListener('click', () => {
      // V4.1 — emit the interaction BEFORE hide() so hide() doesn't
      // misclassify this as an abandonment. Mark interaction-emitted so
      // the same hide call early-outs of the abandoned-emit branch.
      analytics.emit({
        kind: 'eom_rematch_clicked',
        msShown: Math.max(0, Date.now() - this.shownAtMs),
        won: this.lastWon,
      });
      this.interactionEmitted = true;
      this.hide();
      this.opts.onRematch();
    });
    buttons.appendChild(this.rematchBtn);

    this.menuBtn = document.createElement('button');
    this.menuBtn.type = 'button';
    this.menuBtn.className = 'eom__btn';
    this.menuBtn.textContent = 'Back to Menu';
    this.menuBtn.addEventListener('click', () => {
      analytics.emit({
        kind: 'eom_menu_clicked',
        msShown: Math.max(0, Date.now() - this.shownAtMs),
        won: this.lastWon,
      });
      this.interactionEmitted = true;
      this.hide();
      this.opts.onBackToMenu();
    });
    buttons.appendChild(this.menuBtn);
    card.appendChild(buttons);

    this.root.appendChild(card);
  }

  private renderMvp(snap: MatchState | null, youWon: boolean): void {
    if (this.mvpEl === null) return;
    if (snap === null) {
      this.mvpEl.innerHTML = '';
      return;
    }
    const viewerId = this.viewerId;
    const oppId: PlayerId = viewerId === PLAYER_ZERO ? PLAYER_ONE : PLAYER_ZERO;
    const stats = snap.stats;
    const players = snap.players;
    // Compose the headline. Priority order:
    //   1. Streak ≥ 3 (you got rampage/triple etc) — always wins
    //   2. Most squads produced (if margin > 30%)
    //   3. Most kills (from extraStats.viewerKills vs enemyKills)
    //   4. Highest peak income
    //   5. Fallback: "Survived to the end."
    const viewerSquads = stats.squadsBuilt[viewerId];
    const oppSquads = stats.squadsBuilt[oppId];
    const viewerPeak = players[viewerId].peakIncomePerSec;
    const oppPeak = players[oppId].peakIncomePerSec;
    const { viewerKills, enemyKills, bestStreak } = this.extraStats;

    let headline = '';
    let detail = '';
    let icon = '';
    if (bestStreak >= 5) {
      headline = 'SINGULARITY';
      icon = '★';
      detail = `${viewerKills} units eliminated, peak streak ×${bestStreak}.`;
    } else if (bestStreak === 4) {
      headline = 'SYSTEM BREACH';
      icon = '★';
      detail = `${viewerKills} units eliminated, peak streak ×${bestStreak}.`;
    } else if (bestStreak === 3) {
      headline = 'TRIPLE DELETION';
      icon = '★';
      detail = `${viewerKills} units eliminated, peak streak ×${bestStreak}.`;
    } else if (viewerKills > enemyKills && viewerKills >= 4) {
      headline = 'PRECISION OVERRIDE';
      icon = '⚔';
      detail = `${viewerKills} units eliminated vs ${enemyKills}.`;
    } else if (viewerSquads >= oppSquads * 1.3 && viewerSquads >= 6) {
      headline = 'SUPPLY DOMINANCE';
      icon = '⚙';
      detail = `${viewerSquads} squads deployed — out-produced the enemy.`;
    } else if (viewerPeak > oppPeak * 1.2 && viewerPeak >= 8) {
      headline = 'SIGNAL COMMAND';
      icon = '◆';
      detail = `Peak signal income ${viewerPeak.toFixed(1)} S/s.`;
    } else if (youWon) {
      headline = 'MISSION COMPLETE';
      icon = '✦';
      detail = 'A balanced engagement — signal held.';
    } else {
      headline = 'SIGNAL CONTESTED';
      icon = '◇';
      detail = `${viewerKills} eliminations, ${viewerSquads} squads deployed. Re-engage.`;
    }
    this.mvpEl.innerHTML = `
      <div class="eom__mvp-row">
        <span class="eom__mvp-icon">${icon}</span>
        <div class="eom__mvp-text">
          <div class="eom__mvp-title">${escapeText(headline)}</div>
          <div class="eom__mvp-detail">${escapeText(detail)}</div>
        </div>
      </div>
    `;
  }

  private renderStats(snap: MatchState | null): void {
    if (this.statsEl === null) return;
    this.statsEl.innerHTML = '';
    if (snap === null) return;
    const stats = snap.stats;
    const players = snap.players;
    const headerLabels = ['', players[PLAYER_ZERO].name, players[PLAYER_ONE].name];
    const rows: Array<[string, string, string]> = [
      [
        'Squads built',
        String(stats.squadsBuilt[PLAYER_ZERO]),
        String(stats.squadsBuilt[PLAYER_ONE]),
      ],
      [
        'Peak income',
        formatIncome(players[PLAYER_ZERO].peakIncomePerSec),
        formatIncome(players[PLAYER_ONE].peakIncomePerSec),
      ],
      [
        'Relays at end',
        String(countNodes(snap, PLAYER_ZERO)),
        String(countNodes(snap, PLAYER_ONE)),
      ],
      [
        'Doctrine',
        formatDoctrine(players[PLAYER_ZERO].doctrineId),
        formatDoctrine(players[PLAYER_ONE].doctrineId),
      ],
      // V1.5 telemetry — surfaced inline so the human can read the same
      // numbers a playtest spreadsheet will see.
      [
        'Surge time',
        formatSeconds(stats.underdogSurgeSecondsByPlayer[PLAYER_ZERO]),
        formatSeconds(stats.underdogSurgeSecondsByPlayer[PLAYER_ONE]),
      ],
      [
        'Isolated relay time',
        formatSeconds(stats.isolatedRelaySecondsByPlayer[PLAYER_ZERO]),
        formatSeconds(stats.isolatedRelaySecondsByPlayer[PLAYER_ONE]),
      ],
      [
        'Caches picked',
        String(stats.cachesPickedByPlayer[PLAYER_ZERO]),
        String(stats.cachesPickedByPlayer[PLAYER_ONE]),
      ],
    ];

    const table = document.createElement('div');
    table.className = 'eom__stats-table';
    table.appendChild(buildHeaderRow(headerLabels));
    for (const r of rows) {
      table.appendChild(buildStatRow(r));
    }
    this.statsEl.appendChild(table);

    const sparkLabel = document.createElement('div');
    sparkLabel.className = 'eom__spark-label';
    sparkLabel.textContent = 'Relay control over time';
    this.statsEl.appendChild(sparkLabel);

    // V1.7 (2.14) — pass viewerId so the legend can read "You / Enemy"
    // from the right side and pick the correct team color line.
    const spark = renderRelaySparkline(stats.relayHistory, snap, this.viewerId);
    this.statsEl.appendChild(spark);
  }
}

function describeOutcomeHtml(
  outcome: MatchOutcome,
  finalState: MatchState | null,
  viewerId: PlayerId,
  won: boolean,
  extra: ExtraMatchStats,
): string {
  const headline = describeOutcomeHeadline(outcome, finalState, viewerId);
  const tip = won
    ? buildVictoryFlourish(outcome, finalState, viewerId, extra)
    : buildDefeatTip(outcome, finalState, viewerId, extra);
  if (tip === null) return escapeText(headline);
  return `${escapeText(headline)}<div class="eom__tip">${escapeText(tip)}</div>`;
}

function describeOutcomeHeadline(
  outcome: MatchOutcome,
  finalState: MatchState | null,
  viewerId: PlayerId,
): string {
  const youWon = outcome.winner === viewerId;
  if (outcome.kind === 'core_destroyed') {
    return youWon ? 'You destroyed the enemy Core.' : 'Your Core was destroyed.';
  }
  const seconds = finalState !== null ? Math.round(finalState.matchSeconds) : 0;
  if (outcome.kind === 'overtime') {
    const reason = describeOvertimeReason(outcome.reason);
    return `Match ended in overtime at ${formatSeconds(seconds)} — ${reason}.`;
  }
  return `Match timer expired at ${formatSeconds(seconds)} — relay control.`;
}

// V1.9 — defeat tip per CrazyGamesTricks §4.7 / §7.3. The first-loss
// screen should explain WHY the player lost and what to do next, not
// just announce "Defeat". Tips are ordered by severity — the most
// actionable signal wins. Picks at most one line; returns null when no
// specific guidance applies (the prose headline already speaks for
// itself).
function buildDefeatTip(
  outcome: MatchOutcome,
  finalState: MatchState | null,
  viewerId: PlayerId,
  extra: ExtraMatchStats,
): string | null {
  if (finalState === null) return null;
  const relaysHeld = countRelaysFor(finalState, viewerId);
  const squadsBuilt = finalState.stats.squadsBuilt[viewerId] ?? 0;
  const oppId = viewerId === 0 ? 1 : 0;
  const oppSquads = finalState.stats.squadsBuilt[oppId] ?? 0;
  if (relaysHeld === 0) {
    return 'Tip: capture Relays earlier — they generate the Signal you spend on squads.';
  }
  if (squadsBuilt <= 2) {
    return 'Tip: spend Signal faster — click your Core and queue squads as soon as you can afford them.';
  }
  if (oppSquads >= squadsBuilt * 2) {
    return 'Tip: build a Production Hub from your Core to add a second build queue and out-produce the enemy.';
  }
  if (outcome.kind === 'core_destroyed' && extra.viewerKills <= 1) {
    return 'Tip: defend your Core with Shield squads when red units push deep into your side.';
  }
  if (outcome.kind === 'overtime') {
    return 'Tip: in Overtime, holding 2+ Relays is enough — pull squads back to lock down your side.';
  }
  return 'Tip: capture, build, push — every 30 seconds without a Relay capture costs you Signal.';
}

// V1.9 — victory flourish. Optional; surfaces a "well done" line that
// names the strategic move the player did well. Keeps the win moment
// from feeling generic.
function buildVictoryFlourish(
  outcome: MatchOutcome,
  finalState: MatchState | null,
  viewerId: PlayerId,
  extra: ExtraMatchStats,
): string | null {
  if (finalState === null) return null;
  if (outcome.kind === 'core_destroyed') {
    return null; // headline already names the kill — no extra line.
  }
  const relaysHeld = countRelaysFor(finalState, viewerId);
  if (relaysHeld >= 3) return 'Total Signal dominance — the enemy never recovered.';
  if (extra.bestStreak >= 3) return `Streak of ×${extra.bestStreak} closed it out.`;
  return null;
}

function countRelaysFor(state: MatchState, ownerId: PlayerId): number {
  let n = 0;
  for (const node of Object.values(state.controlNodes)) {
    if (node.ownerId === ownerId) n += 1;
  }
  return n;
}

function medalGlyph(tier: 'gold' | 'silver' | 'bronze'): string {
  switch (tier) {
    case 'gold':
      return '★';
    case 'silver':
      return '✦';
    case 'bronze':
      return '◇';
  }
}

function escapeText(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function describeOvertimeReason(
  reason: 'overtime_dominance' | 'core_hp_tiebreak' | 'peak_income_tiebreak',
): string {
  if (reason === 'overtime_dominance') return 'continuous relay lead';
  if (reason === 'core_hp_tiebreak') return 'Core HP tiebreak';
  return 'peak income tiebreak';
}

function formatSeconds(total: number): string {
  const m = Math.floor(total / 60);
  const s = Math.max(0, Math.floor(total - m * 60));
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatIncome(perSec: number): string {
  return `${perSec.toFixed(1)} S/s`;
}

function formatDoctrine(id: 'vanguard' | 'saboteur' | 'bulwark' | null): string {
  if (id === null) return '—';
  return DOCTRINE_DEFS[id].name;
}

function countNodes(snap: MatchState, ownerId: 0 | 1): number {
  let n = 0;
  for (const node of Object.values(snap.controlNodes)) {
    if (node.ownerId === ownerId) n += 1;
  }
  return n;
}

function buildHeaderRow(labels: string[]): HTMLElement {
  const row = document.createElement('div');
  row.className = 'eom__stats-row eom__stats-row--header';
  for (const l of labels) {
    const cell = document.createElement('div');
    cell.className = 'eom__stats-cell';
    cell.textContent = l;
    row.appendChild(cell);
  }
  return row;
}

function buildStatRow(values: [string, string, string]): HTMLElement {
  const row = document.createElement('div');
  row.className = 'eom__stats-row';
  for (let i = 0; i < 3; i++) {
    const cell = document.createElement('div');
    cell.className = i === 0 ? 'eom__stats-cell eom__stats-cell--label' : 'eom__stats-cell';
    cell.textContent = values[i] ?? '';
    row.appendChild(cell);
  }
  return row;
}

const SPARK_W = 320;
const SPARK_H = 80;
// V1.7 (2.14) — asymmetric padding: extra space on the left for Y-axis
// labels ("0", "3"). Previous symmetric SPARK_PAD=6 left no room for axis
// text so the graph was a sparkline with no legend or scale.
const SPARK_PAD_LEFT = 22;
const SPARK_PAD_RIGHT = 6;
const SPARK_PAD_Y = 10;
const TOTAL_RELAYS = 3;

// Source-of-truth: match the in-match team colors from Renderer.ts so the
// post-match graph uses the same blue=player-0 / orange=player-1 mapping the
// player learned during the match. Earlier values (green/red) didn't match
// any in-game element and the player couldn't tell which line was theirs.
const COLOR_P0_HEX = '#4a96ff';
const COLOR_P1_HEX = '#d55a3a';

// Renders a 2-line sparkline of relay counts over time + Y-axis labels (0..3),
// a viewer-keyed legend ("You" vs "Enemy" with team colors), and a one-sentence
// caption explaining the read. The legend / caption are HTML siblings of the
// SVG so they inherit the modal's monospace style without re-injecting font
// data into the SVG.
function renderRelaySparkline(
  history: RelaySample[],
  finalState: MatchState,
  viewerId: PlayerId,
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'eom__spark';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${SPARK_W} ${SPARK_H}`);
  svg.setAttribute('class', 'eom__spark-svg');
  svg.setAttribute('aria-label', 'Relay control over time. Vertical axis: relays held (0 to 3). Two lines, one per player.');

  // Append a synthetic final sample so the sparkline ends at the match end
  // rather than the last cadence sample (which can be up to 4 s short).
  const endSample: RelaySample = {
    tick: finalState.tick,
    matchSeconds: finalState.matchSeconds,
    nodesByPlayer: [
      countNodes(finalState, PLAYER_ZERO),
      countNodes(finalState, PLAYER_ONE),
    ],
  };
  const samples = history.length === 0 ? [endSample] : [...history, endSample];

  // Y-axis: labels at 0 and 3 (the actual max) + a mid dashed line at 1.5 so
  // the "contested" middle reads as a reference line. Text sits in the left
  // pad reserved by SPARK_PAD_LEFT.
  appendYAxis(svg);

  svg.appendChild(buildPath(samples, 0, COLOR_P0_HEX, finalState.matchLengthSec));
  svg.appendChild(buildPath(samples, 1, COLOR_P1_HEX, finalState.matchLengthSec));

  wrap.appendChild(svg);

  // Legend — viewerId determines which color reads as "You". Built in HTML so
  // it inherits the modal's font and tab order naturally.
  wrap.appendChild(buildLegend(viewerId));

  // Caption — one sentence that names what the graph is showing. Players
  // reported confusion at the post-match graph ("what am I looking at?");
  // a single explanatory sentence below the chart closes the gap.
  const caption = document.createElement('div');
  caption.className = 'eom__spark-caption';
  caption.textContent = 'Each line shows how many of the 3 Relays a side held at each moment. The higher the line, the more Signal income that side was earning.';
  wrap.appendChild(caption);

  return wrap;
}

function appendYAxis(svg: SVGSVGElement): void {
  const ns = 'http://www.w3.org/2000/svg';
  const xAxisStart = SPARK_PAD_LEFT;
  const xAxisEnd = SPARK_W - SPARK_PAD_RIGHT;
  const yTop = SPARK_PAD_Y;
  const yBottom = SPARK_H - SPARK_PAD_Y;
  const yMid = (yTop + yBottom) / 2;

  // Dashed mid reference line (was the only y-axis cue in V1.6).
  const mid = document.createElementNS(ns, 'line');
  mid.setAttribute('x1', String(xAxisStart));
  mid.setAttribute('x2', String(xAxisEnd));
  mid.setAttribute('y1', String(yMid));
  mid.setAttribute('y2', String(yMid));
  mid.setAttribute('stroke', '#2a3038');
  mid.setAttribute('stroke-dasharray', '3 3');
  svg.appendChild(mid);

  // Y-axis ticks: "3" at top, "0" at bottom. Anchored end (right-aligned) so
  // the digits sit flush to the chart's left edge.
  for (const [value, y] of [[3, yTop], [0, yBottom]] as const) {
    const text = document.createElementNS(ns, 'text');
    text.setAttribute('x', String(xAxisStart - 5));
    text.setAttribute('y', String(y + 3));
    text.setAttribute('text-anchor', 'end');
    text.setAttribute('font-size', '9');
    text.setAttribute('fill', '#8c95a0');
    text.setAttribute('font-family', 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace');
    text.textContent = String(value);
    svg.appendChild(text);
  }
}

function buildLegend(viewerId: PlayerId): HTMLElement {
  const legend = document.createElement('div');
  legend.className = 'eom__spark-legend';
  // Map viewerId to which color is "You". A viewer playing as PLAYER_ONE sees
  // the orange line as their own; PLAYER_ZERO sees the blue line.
  const youColor = viewerId === PLAYER_ZERO ? COLOR_P0_HEX : COLOR_P1_HEX;
  const enemyColor = viewerId === PLAYER_ZERO ? COLOR_P1_HEX : COLOR_P0_HEX;
  legend.appendChild(buildLegendItem(youColor, 'You'));
  legend.appendChild(buildLegendItem(enemyColor, 'Enemy'));
  return legend;
}

function buildLegendItem(color: string, label: string): HTMLElement {
  const item = document.createElement('span');
  item.className = 'eom__spark-legend-item';
  const swatch = document.createElement('span');
  swatch.className = 'eom__spark-legend-swatch';
  swatch.style.background = color;
  const text = document.createElement('span');
  text.textContent = label;
  item.appendChild(swatch);
  item.appendChild(text);
  return item;
}

function buildPath(
  samples: RelaySample[],
  playerIndex: 0 | 1,
  color: string,
  matchLengthSec: number,
): SVGPathElement {
  const xRange = SPARK_W - SPARK_PAD_LEFT - SPARK_PAD_RIGHT;
  const yRange = SPARK_H - 2 * SPARK_PAD_Y;
  const denom = Math.max(matchLengthSec, samples[samples.length - 1]?.matchSeconds ?? 1);
  const segments: string[] = [];
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i]!;
    const tFrac = denom <= 0 ? 0 : Math.min(1, Math.max(0, s.matchSeconds / denom));
    const x = SPARK_PAD_LEFT + tFrac * xRange;
    const y = SPARK_PAD_Y + (1 - s.nodesByPlayer[playerIndex] / TOTAL_RELAYS) * yRange;
    segments.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', segments.join(' '));
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', color);
  path.setAttribute('stroke-width', '2');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  return path;
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(MODAL_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = MODAL_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${MODAL_ID} {
  position: absolute;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 50;
  background: rgba(2,4,6,0.78);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
}
/* V2 Tempo polish: matches the main-menu Commander card — dual-glow
 * radial backdrop, scanline overlay, hex clip-path, Orbitron banner. */
.eom__card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 36px 56px 32px;
  background:
    radial-gradient(ellipse 100% 100% at 0% 0%, rgba(30, 167, 255, 0.16), transparent 60%),
    radial-gradient(ellipse 100% 100% at 100% 100%, rgba(255, 122, 90, 0.14), transparent 60%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.96), rgba(5, 9, 17, 0.98));
  border: 1px solid rgba(174, 240, 255, 0.32);
  border-radius: 0;
  clip-path: polygon(16px 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%, 0 16px);
  min-width: 460px;
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 44px rgba(30,167,255,0.12) inset,
    0 18px 60px rgba(0,0,0,0.72);
}
.eom__card::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(180deg, rgba(174,240,255,0.04) 1px, transparent 1px);
  background-size: 100% 22px;
  opacity: 0.40;
  pointer-events: none;
}
.eom__card > * { position: relative; z-index: 1; }
.eom__banner {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 44px;
  letter-spacing: 0.30em;
  font-weight: 700;
  text-transform: uppercase;
  color: #ff7a5a;
  text-shadow: 0 0 14px rgba(255,122,90,0.55), 0 0 32px rgba(255,42,42,0.35);
}
.eom__banner[data-win="true"] {
  color: #aef0ff;
  text-shadow: 0 0 14px rgba(110,220,255,0.65), 0 0 32px rgba(30,167,255,0.45);
}
.eom__detail {
  font-size: 13px;
  color: #b8c0c8;
  text-align: center;
  max-width: 380px;
}
.eom__tip {
  margin-top: 6px;
  font-size: 12px;
  color: #f5e76e;
  letter-spacing: 0.02em;
  line-height: 1.45;
}
/* V1.9 — medal chip. Gold/silver/bronze color swatches keyed off the
 * data-tier attribute. Hidden until renderMedal() inserts content. */
.eom__medal {
  display: none;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01));
  border: 1px solid #2a3038;
  width: 100%;
  box-sizing: border-box;
}
.eom__medal[data-tier="gold"] {
  border-color: rgba(245,231,110,0.55);
  background: linear-gradient(135deg, rgba(245,231,110,0.18), rgba(255,178,126,0.05));
  box-shadow: 0 0 20px rgba(245,231,110,0.22);
}
.eom__medal[data-tier="silver"] {
  border-color: rgba(190,210,232,0.50);
  background: linear-gradient(135deg, rgba(200,220,240,0.14), rgba(160,180,210,0.04));
  box-shadow: 0 0 16px rgba(190,210,232,0.18);
}
.eom__medal[data-tier="bronze"] {
  border-color: rgba(208,140,80,0.50);
  background: linear-gradient(135deg, rgba(208,140,80,0.16), rgba(160,90,40,0.04));
  box-shadow: 0 0 16px rgba(208,140,80,0.20);
}
.eom__medal-badge {
  font-size: 28px;
  line-height: 1;
  text-shadow: 0 0 12px currentColor;
}
.eom__medal[data-tier="gold"] .eom__medal-badge { color: #f5e76e; }
.eom__medal[data-tier="silver"] .eom__medal-badge { color: #d8e2ee; }
.eom__medal[data-tier="bronze"] .eom__medal-badge { color: #e09060; }
.eom__medal-text { display: flex; flex-direction: column; gap: 2px; }
.eom__medal-label {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #eef3f8;
}
.eom__medal-detail {
  font-size: 11px;
  color: #b8c0c8;
}
/* V1.9 — next-tier preview chip. Slim row below the unlock card / in
 * its place. Shows "X more matches to unlock <Title>". */
.eom__next {
  display: none;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin: -4px 0 2px;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(106,216,255,0.06);
  border: 1px solid rgba(106,216,255,0.30);
  box-sizing: border-box;
}
.eom__next-chip {
  font-size: 10px;
  letter-spacing: 0.18em;
  color: #6ad8ff;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 3px;
  background: rgba(106,216,255,0.10);
}
.eom__next-text {
  font-size: 12px;
  color: #d4dae2;
  letter-spacing: 0.02em;
}
.eom__next-text strong { color: #f5e76e; font-weight: 700; }
/* V1.9 — XP gain block. Shows level, delta, progress bar, and a tiny
 * source-of-XP breakdown. Width matches the medal/stats above so the
 * card reads as a vertical stack of equal-width rows. */
.eom__xp {
  display: none;
  width: 100%;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(58,123,213,0.06);
  border: 1px solid rgba(106,216,255,0.30);
  box-sizing: border-box;
}
.eom__xp-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.eom__xp-level {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.16em;
  color: #6ad8ff;
  padding: 2px 8px;
  border-radius: 3px;
  background: rgba(106,216,255,0.12);
}
.eom__xp-delta {
  font-size: 14px;
  font-weight: 800;
  color: #f5e76e;
  letter-spacing: 0.04em;
}
.eom__xp-levelup {
  margin-left: auto;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.22em;
  color: #fff5d4;
  padding: 3px 8px;
  border-radius: 3px;
  background: rgba(245,231,110,0.18);
  border: 1px solid rgba(245,231,110,0.55);
  text-transform: uppercase;
  animation: eom-unlock-pop 0.6s 0.1s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.eom__xp-bar {
  height: 6px;
  background: rgba(255,255,255,0.06);
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid rgba(106,216,255,0.18);
}
.eom__xp-fill {
  height: 100%;
  background: linear-gradient(90deg, #6ad8ff, #f5e76e);
  border-radius: 999px;
  transition: width 480ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.eom__xp-progress {
  margin-top: 4px;
  font-size: 10px;
  color: #8c95a0;
  letter-spacing: 0.06em;
}
.eom__xp-breakdown {
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.eom__xp-chip {
  font-size: 10px;
  color: #b8c0c8;
  padding: 2px 6px;
  border-radius: 3px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  letter-spacing: 0.02em;
}
.eom__mvp {
  width: 100%;
  margin: 4px 0 6px;
}
.eom__unlock {
  width: 100%;
  margin: 0 0 10px;
  padding: 12px 18px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(245, 231, 110, 0.16), rgba(255, 178, 126, 0.08));
  border: 1px solid rgba(245, 231, 110, 0.55);
  box-shadow: 0 0 24px rgba(245, 231, 110, 0.28);
  text-align: center;
  animation: eom-unlock-pop 0.6s 0.1s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.eom__unlock-chip {
  font-size: 10px;
  letter-spacing: 0.22em;
  color: #f5e76e;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.eom__unlock-title {
  font-size: 20px;
  font-weight: 700;
  color: #fff5d4;
  letter-spacing: 0.06em;
  text-shadow: 0 0 14px rgba(245, 231, 110, 0.6);
}
.eom__unlock-desc {
  margin-top: 2px;
  font-size: 12px;
  color: #b8c0cc;
}
@keyframes eom-unlock-pop {
  0%   { opacity: 0; transform: scale(0.92); }
  60%  { opacity: 1; transform: scale(1.04); }
  100% { opacity: 1; transform: scale(1.00); }
}
.eom__mvp-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 18px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(245, 231, 110, 0.10), rgba(105, 180, 255, 0.06));
  border: 1px solid rgba(245, 231, 110, 0.32);
  box-shadow: 0 0 18px rgba(245, 231, 110, 0.18);
}
.eom__mvp-icon {
  font-size: 30px;
  color: #f5e76e;
  text-shadow: 0 0 12px currentColor;
  line-height: 1;
}
.eom__mvp-text { display: flex; flex-direction: column; gap: 2px; }
.eom__mvp-title {
  font-size: 16px;
  font-weight: 800;
  color: #f5e76e;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.eom__mvp-detail {
  font-size: 12px;
  color: #c8cfd8;
}
.eom__stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  width: 100%;
}
.eom__stats-table {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #2a3038;
  border-bottom: 1px solid #2a3038;
}
.eom__stats-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  align-items: center;
  padding: 6px 4px;
  font-size: 12px;
}
.eom__stats-row--header {
  color: #8c95a0;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-bottom: 1px solid #2a3038;
}
.eom__stats-cell {
  text-align: right;
  color: #cfd6dd;
}
.eom__stats-cell--label {
  text-align: left;
  color: #8c95a0;
}
.eom__spark-label {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8c95a0;
  margin-top: 4px;
}
.eom__spark {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.eom__spark-svg {
  width: 100%;
  max-width: ${SPARK_W}px;
  height: ${SPARK_H}px;
  background: rgba(0,0,0,0.25);
  border: 1px solid #2a3038;
  border-radius: 4px;
}
/* V1.7 (2.14) — legend + caption below the sparkline so the player can read
 * which line is theirs and what the axis means. Without these, the graph
 * was a pair of unlabeled lines that confused first-time viewers. */
.eom__spark-legend {
  display: flex;
  gap: 16px;
  font-size: 11px;
  color: #b8c0c8;
  letter-spacing: 0.04em;
}
.eom__spark-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.eom__spark-legend-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  border: 1px solid rgba(255,255,255,0.18);
}
.eom__spark-caption {
  font-size: 11px;
  color: #8c95a0;
  text-align: center;
  max-width: ${SPARK_W}px;
  line-height: 1.45;
  letter-spacing: 0.02em;
}
.eom__buttons {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
/* V2 Tempo — Rank chip. Tier color is injected via --rank-color so
 * the same DOM shape adapts to Bronze/Silver/Gold/Diamond/Master. */
.eom__rank {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  background: rgba(10, 19, 34, 0.55);
  border: 1px solid rgba(174, 240, 255, 0.22);
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  transition: border-color 0.5s ease, box-shadow 0.5s ease;
}
.eom__rank--promoted {
  border-color: rgba(255, 230, 128, 0.65);
  box-shadow: 0 0 22px rgba(255, 230, 128, 0.35);
  animation: eom-rank-promo 1.4s ease-out;
}
.eom__rank--demoted {
  border-color: rgba(255, 122, 90, 0.45);
  box-shadow: 0 0 14px rgba(255, 122, 90, 0.20);
}
.eom__rank-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.eom__rank-tier {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.22em;
  flex: 1;
}
.eom__rank-elo {
  font-size: 12px;
  color: #a3b8d2;
  letter-spacing: 0.10em;
}
.eom__rank-delta {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.06em;
  min-width: 48px;
  text-align: right;
}
.eom__rank-delta--up { color: #6fdcff; text-shadow: 0 0 10px rgba(110,220,255,0.55); }
.eom__rank-delta--down { color: #ff7a5a; text-shadow: 0 0 8px rgba(255,122,90,0.45); }
.eom__rank-delta--flat { color: #6a7d96; }
.eom__rank-promo {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: #ffe680;
  text-shadow: 0 0 10px rgba(255, 230, 128, 0.55);
}
.eom__rank-demo {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: #ff7a5a;
}
.eom__rank-peak {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: #c66bff;
  text-shadow: 0 0 10px rgba(198, 107, 255, 0.55);
}
@keyframes eom-rank-promo {
  0%   { transform: scale(1.0); box-shadow: 0 0 8px rgba(255,230,128,0.10); }
  25%  { transform: scale(1.04); box-shadow: 0 0 30px rgba(255,230,128,0.65); }
  100% { transform: scale(1.0); box-shadow: 0 0 22px rgba(255,230,128,0.35); }
}

/* V2 Tempo polish: button vocabulary matches the main-menu PLAY NOW
 * chevron — hex clip-path, Orbitron caps, cyan rim glow. Primary uses
 * the blue→red rim gradient for the dominant rematch action. */
.eom__btn {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  padding: 14px 28px;
  background: linear-gradient(180deg, rgba(20,28,38,0.85), rgba(10,14,20,0.92));
  color: #cfd6dd;
  border: 1px solid rgba(174,240,255,0.28);
  border-radius: 0;
  clip-path: polygon(10px 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0 50%);
  cursor: pointer;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  transition: color 150ms, box-shadow 150ms, transform 80ms;
  min-height: 44px;
}
.eom__btn--primary {
  background: linear-gradient(180deg, rgba(30, 167, 255, 0.20), rgba(255, 42, 42, 0.10));
  border-color: rgba(174,240,255,0.55);
  color: #eef4ff;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(174,240,255,0.45);
  box-shadow: 0 0 18px rgba(30,167,255,0.30), 0 0 30px rgba(255,42,42,0.15);
}
.eom__btn:hover {
  color: #aef0ff;
  box-shadow: 0 0 18px rgba(30,167,255,0.40);
}
.eom__btn--primary:hover {
  color: #ffffff;
  box-shadow: 0 0 28px rgba(30,167,255,0.55), 0 0 40px rgba(255,42,42,0.25);
}
.eom__btn:active { transform: scale(0.98); }
.eom__btn:focus-visible {
  outline: 2px solid #aef0ff;
  outline-offset: 3px;
}

/* V3 mobile: EOM modal is the player's reflection moment + the "rematch /
 * back" decision. On a phone we shrink fonts and pad less, but the buttons
 * stay finger-sized. Stats row uses smaller fonts but the SVG sparkline
 * scales by container width so it stays readable. */
@media (max-width: 820px), (pointer: coarse) {
  .eom__card {
    min-width: 0;
    width: calc(100vw - 24px);
    max-width: 480px;
    padding: 20px 18px;
    gap: 10px;
    box-sizing: border-box;
    max-height: calc(100vh - 24px);
    overflow-y: auto;
  }
  .eom__banner { font-size: 26px; letter-spacing: 0.16em; }
  .eom__detail { font-size: 12px; max-width: 320px; }
  .eom__stats-row { font-size: 11px; padding: 5px 2px; }
  /* Bump from 9 px — that was illegible at arm's length on any phone. */
  .eom__stats-row--header { font-size: 11px; }
  /* Prevent long labels ("Isolated relay time") from overflowing a ~96 px
   * label column on the smallest portrait phones. */
  .eom__stats-cell--label {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .eom__buttons { gap: 10px; flex-wrap: wrap; justify-content: center; }
  .eom__btn { padding: 12px 18px; min-height: 48px; font-size: 13px; }
}
`;
  document.head.appendChild(style);
}
